/**
 * Grey Bandit — Quick Shop Color Swatch Prefetch
 * -----------------------------------------------
 * When a Quick Shop modal opens, silently pre-fetches and caches every sibling
 * color-variant modal in the background.  This populates the theme's internal
 * `cachedFetch` Map so that the *first* click on any color swatch is as fast
 * as subsequent clicks on the same swatch.
 *
 * HOW IT WORKS
 * ------------
 * The theme's `quick-buy-modal` custom element exposes a `show()` method that
 * calls an internal `cachedFetch()` helper (a module-level closure wrapping
 * `window.fetch` with an in-memory Map).  All color-variant modals for a single
 * product share the same `productid` attribute, which is the Shopify Section
 * Rendering API ref used to build the fetch URL:
 *
 *   /products/{handle}?ref={productid}
 *
 * Because the server sets `cf-cache-status: DYNAMIC`, responses are NOT stored
 * in the browser's HTTP cache — only the in-memory Map helps.  To populate it
 * for every colour without triggering visible UI changes we must call the theme's
 * own `show()` on each sibling, then immediately `hide()` it once its content
 * has loaded.
 *
 * INSTALLATION
 * ------------
 * Paste this file's content into a new JS asset in the Shopify theme and include
 * it with a <script defer src="..."> tag near the bottom of theme.liquid, OR add
 * it inside an existing deferred script block.  It uses `customElements.whenDefined`
 * so it is safe to load before or after theme.js.
 *
 * TESTED ON: Grey Bandit theme — greybandit.com — Feb 2026
 */

(function () {
  'use strict';

  /**
   * Silently invoke `show()` on a sibling modal to trigger its internal
   * `cachedFetch` call, then immediately `hide()` it once content has loaded.
   *
   * Three overrides are needed to avoid locking the page or stealing focus:
   *   1. `shouldLock`      — prevents <body> scroll lock
   *   2. `shouldTrapFocus` — prevents focus-trap activation
   *   3. `initialFocus`    — prevents focus being moved to the modal
   *
   * All three are getter-only prototype properties so we must use
   * Object.defineProperty with { configurable: true } to shadow them
   * at the instance level, and delete the instance properties to restore
   * prototype behaviour after hide().
   *
   * @param {HTMLElement} sibling  — quick-buy-modal element to pre-fetch
   * @param {Function}    themeShow — original (unpatched) show() method
   */
  function prefetchSibling(sibling, themeShow) {
    // Skip if already populated (children > 0 means content is cached in DOM)
    if (sibling.children.length > 0) return;

    // 1. Shadow the getter-only prototype properties so the modal opens silently
    Object.defineProperty(sibling, 'shouldLock',      { get: () => false, configurable: true });
    Object.defineProperty(sibling, 'shouldTrapFocus', { get: () => false, configurable: true });
    Object.defineProperty(sibling, 'initialFocus',    { get: () => null,  configurable: true });

    // 2. Visually hide the element so it never appears on screen.
    //    visibility:hidden + position:fixed keeps it out of the layout and off-screen.
    sibling.style.cssText =
      'visibility:hidden!important;pointer-events:none!important;' +
      'position:fixed!important;z-index:-9999!important;top:0!important;left:0!important;';

    // 3. Watch for content to arrive, then immediately close and clean up
    const observer = new MutationObserver(function () {
      if (sibling.children.length > 0) {
        observer.disconnect();

        // hide() removes the `open` attribute (closes the modal) but preserves
        // innerHTML, so cachedFetch's Map entry — and the rendered DOM — remain.
        sibling.hide();

        // Restore styles and remove instance-level property overrides
        sibling.style.cssText = '';
        delete sibling.shouldLock;
        delete sibling.shouldTrapFocus;
        delete sibling.initialFocus;
      }
    });
    observer.observe(sibling, { childList: true });

    // 4. Trigger the theme's original show() — this is what calls cachedFetch internally
    themeShow.call(sibling);
  }

  /**
   * Patch QuickBuyModal.prototype.show so that every time a modal is opened
   * we schedule background prefetches for all sibling color-variant modals.
   *
   * @param {Function} QBM — the QuickBuyModal class returned by customElements.get()
   */
  function patchQuickBuyModal(QBM) {
    const _themeShow = QBM.prototype.show;

    QBM.prototype.show = function (trigger) {
      // Always open the requested modal immediately — no delay for the user
      const result = _themeShow.call(this, trigger);

      // Defer sibling prefetches to the next animation frame so the main
      // modal's own fetch/render is not blocked by the prefetch loop.
      const productId = this.getAttribute('productid');
      if (productId) {
        const self = this;
        requestAnimationFrame(function () {
          // Build a Set of handles that are sold out within this product card.
          // Structure: <product-card> contains both the .swatch-products list and
          // all quick-buy-modal elements. Sold-out swatches are <li class="sold-out">
          // whose <input data-variant-url="/products/{handle}"> gives us the handle.
          const card = self.closest('product-card');
          const soldOutHandles = new Set();
          if (card) {
            card.querySelectorAll(
              'li.HorizontalList__Item.sold-out input[data-variant-url]'
            ).forEach(function (input) {
              const url = input.getAttribute('data-variant-url') || '';
              const handle = url.split('/').pop();
              if (handle) soldOutHandles.add(handle);
            });
          }

          document
            .querySelectorAll('quick-buy-modal[productid="' + productId + '"]')
            .forEach(function (sibling) {
              // Skip the modal that was just opened by the user
              if (sibling === self) return;
              // Skip sold-out variants — their swatches are unclickable so there
              // is no benefit to prefetching them
              if (soldOutHandles.has(sibling.getAttribute('handle'))) return;
              prefetchSibling(sibling, _themeShow);
            });
        });
      }

      return result;
    };
  }

  // Wait until the custom element is registered (safe whether this script
  // runs before or after theme.js defines quick-buy-modal).
  customElements.whenDefined('quick-buy-modal').then(function () {
    patchQuickBuyModal(customElements.get('quick-buy-modal'));
  });
})();
